import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { RECAPTCHA_SITE_KEY } from "../config";

// Google reCAPTCHA (the "I'm not a robot" tick box) for the public forms.
//
// It is switched on by putting the site key in REACT_APP_RECAPTCHA_SITE_KEY.
// With no key, nothing is shown and the forms work as they did before.
// The API checks the answer with the matching secret key (RECAPTCHA_SECRET_KEY
// in the API's .env); see app/Http/Middleware/VerifyRecaptcha.php there.
//
// In a form:
//     const robot = useRecaptcha();
//     ...
//     if (robot.missing) { show ROBOT_MESSAGE and stop }
//     send  recaptcha_token: robot.token  with the rest of the form
//     robot.reset() once the API has answered (an answer can be used only once)
//     ...
//     {robot.element}   just above the form's button

export const RECAPTCHA_ON = Boolean(RECAPTCHA_SITE_KEY);

export const ROBOT_MESSAGE = "Please tick \"I'm not a robot\" before you continue.";

const SCRIPT_URL = "https://www.google.com/recaptcha/api.js?render=explicit&onload=cfcmRecaptchaReady";

// Google's script is added to the page once, the first time a form needs it.
let scriptPromise = null;

const loadScript = () => {
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
        if (window.grecaptcha && window.grecaptcha.render) {
            resolve(window.grecaptcha);
            return;
        }

        window.cfcmRecaptchaReady = () => resolve(window.grecaptcha);

        const script = document.createElement("script");
        script.src = SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
            scriptPromise = null; // Let the next form try again.
            reject(new Error("reCAPTCHA could not be loaded"));
        };
        document.head.appendChild(script);
    });

    return scriptPromise;
};

const Recaptcha = forwardRef(function Recaptcha({ onChange }, ref) {
    const containerRef = useRef(null);
    const widgetId = useRef(null);
    const onChangeRef = useRef(onChange);
    const [failed, setFailed] = useState(false);

    onChangeRef.current = onChange;

    useEffect(() => {
        if (!RECAPTCHA_ON) return undefined;

        let cancelled = false;
        let holder = null;
        const container = containerRef.current;

        loadScript()
            .then((grecaptcha) => {
                if (cancelled || !container) return;

                // Each time the box is drawn it gets an element of its own.
                holder = document.createElement("div");
                container.appendChild(holder);

                widgetId.current = grecaptcha.render(holder, {
                    sitekey: RECAPTCHA_SITE_KEY,
                    callback: (token) => onChangeRef.current && onChangeRef.current(token),
                    "expired-callback": () => onChangeRef.current && onChangeRef.current(""),
                    "error-callback": () => onChangeRef.current && onChangeRef.current(""),
                });
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });

        return () => {
            cancelled = true;
            widgetId.current = null;
            if (holder && holder.parentNode) holder.parentNode.removeChild(holder);
        };
    }, []);

    useImperativeHandle(ref, () => ({
        reset() {
            if (widgetId.current !== null && window.grecaptcha) {
                try {
                    window.grecaptcha.reset(widgetId.current);
                } catch (error) {
                    // The box has already gone from the page.
                }
            }
        },
    }));

    if (!RECAPTCHA_ON) return null;

    return (
        <div className="cf-recaptcha">
            <div ref={containerRef}></div>
            {failed && (
                <p className="help is-danger">
                    The "I'm not a robot" check could not be loaded. Check your internet connection and reload the page.
                </p>
            )}
        </div>
    );
});

// What a form needs: the box to show, the answer to send, and a way to clear it.
export function useRecaptcha() {
    const ref = useRef(null);
    const [token, setToken] = useState("");

    const reset = () => {
        if (ref.current) ref.current.reset();
        setToken("");
    };

    return {
        element: <Recaptcha ref={ref} onChange={setToken} />,
        token,
        // True when the box must be ticked and has not been.
        missing: RECAPTCHA_ON && !token,
        reset,
    };
}

export default Recaptcha;
